"use client";

import { useEffect, useState } from "react";
import { Menu, Search, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  if (loading) {
    return <>
        <div className="hidden md:flex gap-1">
          <div className="h-9 w-16 bg-secondary/20 animate-pulse rounded-md" />
          <div className="h-9 w-16 bg-secondary/20 animate-pulse rounded-md" />
          <div className="h-9 w-16 bg-secondary/20 animate-pulse rounded-md" />
          <div className="h-9 w-16 bg-secondary/20 animate-pulse rounded-md" />
          <div className="h-9 w-16 bg-secondary/20 animate-pulse rounded-md" />
        </div>
        <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" aria-label="Menu">
          <Menu className="size-5" />
        </Button>
      </>;
  }
  return <>
      <NavigationMenu viewport={false} className="hidden md:flex">
        <NavigationMenuList>
          {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="relative px-4 py-2 text-sm font-medium text-foreground transition-all duration-[var(--duration-medium)] hover:!bg-transparent focus:!bg-transparent after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all after:duration-[var(--duration-medium)] hover:after:w-full">
                  {item.label}
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>)}
        </NavigationMenuList>
      </NavigationMenu>

      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
        {mobileMenuOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {mobileMenuOpen && <div className="absolute top-full left-0 right-0 bg-background border-t border-border shadow-medium md:hidden z-50">
          <nav className="flex flex-col p-4 gap-2">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="px-4 py-3 text-sm font-medium text-foreground rounded-md hover:bg-accent hover:text-accent-foreground transition-all duration-[var(--duration-fast)]" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>)}
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const handleRequestDemo = () => {
    const contactForm = document.querySelector("#contact-form");
    if (contactForm) {
      contactForm.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <header id="header" className={`sticky top-0 z-50 w-full bg-background border-b border-border transition-all duration-[var(--duration-medium)] ease-[var(--ease-out-back)] ${scrolled ? "h-16 shadow-medium" : "h-24"}`}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1536499/img/ampkore_logo_primary.png" alt="AMPKORE" className="h-7" />
        </Link>

        <MenuProvider menu_id="36" className="flex items-center">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="hidden md:flex items-center gap-3">
          <Button variant="ghost" size="icon" className="hover:!bg-transparent focus:!bg-transparent transition-all duration-[var(--duration-fast)] hover:scale-110" aria-label="Search">
            <Search className="size-5" />
          </Button>

          <Button onClick={handleRequestDemo} className="hover:!bg-transparent focus:!bg-transparent bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-foreground transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
            Request Demo
          </Button>
        </div>
      </div>
    </header>;
}